import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PipelineStatusBanner } from '../components/PipelineStatusBanner';
import { Surface } from '../components/Surface';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type LaborForce = {
  areaCode: string;
  areaTitle: string;
  periodYear: number;
  periodMonth: number;
  laborForce: number | null;
  employed: number | null;
  unemployed: number | null;
  unemploymentRate: number | null;
  sourceUrl: string;
};

type IndustryRow = {
  industryCode: string;
  industryTitle: string;
  avgEmployment: number | null;
  avgWeeklyWage: number | null;
  locationQuotient: number | null;
  releaseYear: number;
};

type OccupationRow = {
  socCode: string;
  occupationTitle: string;
  employmentEstimate: number | null;
  medianWage: number | null;
  releaseYear: number;
};

type CommutingFlows = {
  inflow: number;
  outflow: number;
  selfContained: number;
  releaseYear: number;
};

type GapRow = {
  naicsCode: string;
  industryTitle: string;
  industryEmployment: number;
  occupationTitle: string | null;
  occupationalSupply: number | null;
  supplyRatio: number | null;
  gapLabel: 'tight' | 'available' | 'unknown';
};

type EducationSnapshot = {
  countyFips: string;
  surveyYear: number;
  totalPop25: number;
  lessThanHs: number | null;
  hsOrGed: number | null;
  someCollege: number | null;
  bachelors: number | null;
  graduate: number | null;
};

type WorkforceProfile = {
  geography: { countyFips: string; areaName: string; oesAreaCode: string | null } | null;
  laborForce: LaborForce | null;
  industries: IndustryRow[];
  occupations: OccupationRow[];
  commuting: CommutingFlows | null;
  occupationalGap: GapRow[];
  education: EducationSnapshot | null;
  citations: Record<string, string>;
  reason: string;
};

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function fmt(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtCur(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function NoData({ label }: { label: string }) {
  return (
    <p className="py-6 text-center text-sm text-slate-400">
      No {label} data loaded — run the data pipeline first.
    </p>
  );
}

function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function LaborForcePanel({ data }: { data: LaborForce }) {
  const month = MONTH_NAMES[(data.periodMonth - 1) % 12] ?? '';
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {[
        { label: 'Labor Force', value: fmt(data.laborForce) },
        { label: 'Employed', value: fmt(data.employed) },
        { label: 'Unemployed', value: fmt(data.unemployed) },
        {
          label: 'Unemployment Rate',
          value: data.unemploymentRate != null ? `${data.unemploymentRate.toFixed(1)}%` : '—',
        },
      ].map(({ label, value }) => (
        <div key={label} className="rounded-xl bg-slate-50 p-4 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-xl font-bold text-slate-800">{value}</p>
        </div>
      ))}
      <p className="col-span-full text-[11px] text-slate-400">
        {data.areaTitle} · {month} {data.periodYear}
      </p>
    </div>
  );
}

function IndustryPanel({ rows }: { rows: IndustryRow[] }) {
  const chartData = rows
    .filter((r) => r.avgEmployment)
    .slice(0, 8)
    .map((r) => ({
      name: r.industryTitle.replace(/[^a-zA-Z &,]/g, '').slice(0, 28),
      value: r.avgEmployment,
    }));

  return (
    <div className="space-y-4">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 10 }}
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
          />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={160} />
          <Tooltip formatter={(v) => [fmt(Number(v)), 'Avg employment']} />
          <Bar dataKey="value" radius={[0, 3, 3, 0]}>
            {chartData.map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: Recharts Cell has no stable id
              <Cell key={i} fill={i < 3 ? '#0f766e' : '#94a3b8'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-[11px] text-slate-400">
            <th className="pb-1 text-left font-medium">Industry (NAICS {rows[0]?.releaseYear})</th>
            <th className="pb-1 text-right font-medium">Avg Jobs</th>
            <th className="pb-1 text-right font-medium">Avg Weekly Wage</th>
            <th className="pb-1 text-right font-medium">Location Quotient</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => (
            <tr key={r.industryCode} className="hover:bg-slate-50">
              <td className="py-1.5 text-slate-700">{r.industryTitle}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-600">
                {fmt(r.avgEmployment)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-slate-600">
                {fmtCur(r.avgWeeklyWage)}
              </td>
              <td className="py-1.5 text-right tabular-nums">
                {r.locationQuotient != null ? (
                  <span
                    className={
                      r.locationQuotient >= 1.25 ? 'font-semibold text-teal-700' : 'text-slate-500'
                    }
                  >
                    {r.locationQuotient.toFixed(2)}
                  </span>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MethodologyNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
      <span className="mr-1 font-semibold text-slate-400 uppercase tracking-wide">Methodology</span>
      {children}
    </div>
  );
}

function OccupationsPanel({ rows }: { rows: OccupationRow[] }) {
  return (
    <div>
      <MethodologyNote>
        BLS OES publishes occupation data at the <strong>state level only</strong> — no county or
        city breakdowns are available via the public API. Employment figures shown here are{' '}
        <strong>county-level estimates</strong> derived by scaling statewide OES employment totals
        by the ratio of county employed workers (BLS LAUS) to total state OES employment. This is a
        proportional approximation; actual county occupational employment will differ. Median wages
        reflect statewide BLS OES figures and are not adjusted for local cost-of-living differences.
      </MethodologyNote>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-[11px] text-slate-400">
            <th className="pb-1 text-left font-medium">Occupation</th>
            <th className="pb-1 text-right font-medium">SOC</th>
            <th className="pb-1 text-right font-medium">Employment</th>
            <th className="pb-1 text-right font-medium">Median Wage</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => (
            <tr key={r.socCode} className="hover:bg-slate-50">
              <td className="py-1.5 text-slate-700">{r.occupationTitle}</td>
              <td className="py-1.5 text-right text-xs text-slate-400 tabular-nums">{r.socCode}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-600">
                {fmt(r.employmentEstimate)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-slate-600">
                {fmtCur(r.medianWage)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommutingPanel({ data }: { data: CommutingFlows }) {
  const total = data.inflow + data.outflow + data.selfContained;
  const pct = (n: number) => (total > 0 ? `${((n / total) * 100).toFixed(0)}%` : '—');

  const chartData = [
    { name: 'Live & work here', value: data.selfContained, color: '#0f766e' },
    { name: 'Commute in', value: data.inflow, color: '#0891b2' },
    { name: 'Commute out', value: data.outflow, color: '#f59e0b' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {chartData.map(({ name, value, color }) => (
          <div key={name} className="rounded-xl bg-slate-50 p-4 text-center">
            <div className="mx-auto mb-1 h-2 w-8 rounded-full" style={{ backgroundColor: color }} />
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {name}
            </p>
            <p className="mt-1 text-lg font-bold text-slate-800">{fmt(value)}</p>
            <p className="text-xs text-slate-400">{pct(value)}</p>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
          <Tooltip formatter={(v) => [fmt(Number(v)), 'Workers']} />
          <Bar dataKey="value" radius={[3, 3, 0, 0]}>
            {chartData.map((d, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: Recharts Cell has no stable id
              <Cell key={i} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-[11px] text-slate-400">
        Net commute balance: {data.inflow >= data.outflow ? '+' : ''}
        {fmt(data.inflow - data.outflow)} (positive = more workers flow in) · LEHD{' '}
        {data.releaseYear}
      </p>
    </div>
  );
}

function GapPanel({ rows }: { rows: GapRow[] }) {
  return (
    <div>
      <MethodologyNote>
        Industry employment is exact county-level data from QCEW. Occupational pool estimates are
        county approximations scaled from state OES data (see Occupational Availability above) — not
        directly observed local counts. The ratio shows estimated pool depth relative to industry
        demand; a higher ratio means more workers in this occupational group proportionally to local
        industry jobs. All figures are directional — they do not measure unemployed or available
        workers.
      </MethodologyNote>
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col className="w-[33%]" />
          <col className="w-[27%]" />
          <col className="w-[16%]" />
          <col className="w-[14%]" />
          <col className="w-[10%]" />
        </colgroup>
        <thead>
          <tr className="border-b border-slate-100 text-[11px] text-slate-400">
            <th className="pb-1 text-left font-medium">Industry</th>
            <th className="pb-1 text-left font-medium">Primary Occ Group</th>
            <th className="pb-1 text-right font-medium whitespace-nowrap">Industry Jobs</th>
            <th className="pb-1 text-right font-medium whitespace-nowrap">Est. Pool</th>
            <th className="pb-1 text-right font-medium">Ratio</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => (
            <tr key={r.naicsCode} className="hover:bg-slate-50">
              <td className="py-1.5 text-slate-700 truncate pr-2">{r.industryTitle}</td>
              <td className="py-1.5 text-slate-500 truncate pr-2">{r.occupationTitle ?? '—'}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
                {fmt(r.industryEmployment)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
                {fmt(r.occupationalSupply)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
                {r.supplyRatio != null ? `${r.supplyRatio.toFixed(1)}×` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EducationPanel({ data }: { data: EducationSnapshot }) {
  const total = data.totalPop25;
  const pct = (n: number | null) =>
    n == null || total === 0 ? null : Math.round((n / total) * 1000) / 10;

  const rows = [
    { label: 'Less than high school', value: data.lessThanHs, color: '#f87171' },
    { label: 'High school / GED', value: data.hsOrGed, color: '#fb923c' },
    { label: "Some college / Associate's", value: data.someCollege, color: '#facc15' },
    { label: "Bachelor's degree", value: data.bachelors, color: '#4ade80' },
    { label: 'Graduate / Professional', value: data.graduate, color: '#60a5fa' },
  ];

  const chartData = rows
    .filter((r) => r.value != null)
    .map((r) => ({ name: r.label, pct: pct(r.value) ?? 0, color: r.color }));

  return (
    <div>
      <MethodologyNote>
        Population 25 and older. County-level data from the Census ACS 5-year estimates — the most
        granular geography available. Figures reflect the resident population's educational
        background, not job-seeker counts.
      </MethodologyNote>
      <div className="mb-3 text-xs text-slate-500">
        Total population 25+: <span className="font-semibold text-slate-700">{fmt(total)}</span> (
        {data.surveyYear} ACS 5-year)
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ left: 8, right: 40, top: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11 }}
            domain={[0, 'dataMax + 5']}
          />
          <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Share']} />
          <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function WorkforceProfileRoute() {
  const getToken = useSessionTokenGetter();
  const [profile, setProfile] = useState<WorkforceProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  async function downloadPdf() {
    setDownloading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/workforce-profile/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workforce-profile-${profile?.geography?.countyFips ?? 'export'}-${new Date().getFullYear()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/workforce-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) setProfile((await res.json()) as WorkforceProfile);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-sm text-slate-400">Loading workforce profile…</p>
      </div>
    );
  }

  if (!profile?.geography) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-2 text-2xl font-bold text-slate-900">Workforce Profile</h1>
        <Surface className="p-6">
          <p className="text-sm text-amber-700">
            No geography configured. Add a County FIPS code to your default Jurisdiction Profile in{' '}
            <a href="/settings/jurisdiction" className="underline">
              Settings → Jurisdiction
            </a>
            .
          </p>
        </Surface>
      </div>
    );
  }

  const {
    laborForce,
    industries,
    occupations,
    commuting,
    occupationalGap,
    education,
    citations,
    geography,
  } = profile;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Workforce Profile</h1>
          <p className="mt-1 text-sm text-slate-500">
            {geography.areaName} · County FIPS {geography.countyFips}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void downloadPdf()}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {downloading ? 'Generating…' : 'Download PDF'}
        </button>
      </div>

      <PipelineStatusBanner />

      {/* 1 — Labor Force Overview */}
      <Surface className="p-5">
        <SectionHeader
          title="Labor Force Overview"
          sub="BLS Local Area Unemployment Statistics (LAUS)"
        />
        {laborForce ? <LaborForcePanel data={laborForce} /> : <NoData label="LAUS" />}
        {citations.laus && <p className="mt-3 text-[11px] text-slate-400">{citations.laus}</p>}
      </Surface>

      {/* 2 — Industry Employment */}
      <Surface className="p-5">
        <SectionHeader
          title="Employment by Industry"
          sub="Private-sector employment by 2-digit NAICS sector (BLS QCEW)"
        />
        {industries.length > 0 ? <IndustryPanel rows={industries} /> : <NoData label="QCEW" />}
        {citations.qcew && <p className="mt-3 text-[11px] text-slate-400">{citations.qcew}</p>}
      </Surface>

      {/* 3 — Occupational Availability */}
      <Surface className="p-5">
        <SectionHeader
          title="Occupational Availability"
          sub="Top occupations by estimated local employment (BLS OES)"
        />
        {occupations.length > 0 ? <OccupationsPanel rows={occupations} /> : <NoData label="OES" />}
        {citations.oes && <p className="mt-3 text-[11px] text-slate-400">{citations.oes}</p>}
      </Surface>

      {/* 4 — Educational Attainment */}
      <Surface className="p-5">
        <SectionHeader
          title="Educational Attainment"
          sub="Population 25+ by highest degree attained (Census ACS 5-year)"
        />
        {education ? (
          <EducationPanel data={education} />
        ) : (
          <NoData label="ACS educational attainment" />
        )}
        {citations.acs && <p className="mt-3 text-[11px] text-slate-400">{citations.acs}</p>}
      </Surface>

      {/* 5 — Commuting Patterns */}
      <Surface className="p-5">
        <SectionHeader
          title="Commuting Patterns"
          sub="Worker inflow / outflow by home and work county (Census LEHD LODES)"
        />
        {commuting ? <CommutingPanel data={commuting} /> : <NoData label="LEHD" />}
        {citations.lehd && <p className="mt-3 text-[11px] text-slate-400">{citations.lehd}</p>}
      </Surface>

      {/* 6 — Occupational Pool Depth */}
      <Surface className="p-5">
        <SectionHeader
          title="Occupational Pool Depth by Industry"
          sub="Estimated local occupational pool (scaled state OES) relative to county industry employment (QCEW)"
        />
        {occupationalGap.length > 0 ? (
          <GapPanel rows={occupationalGap} />
        ) : (
          <NoData label="gap analysis (requires both QCEW and OES data)" />
        )}
      </Surface>
    </div>
  );
}
